// Office "Data retention" page (Session ASQ, Stream G, Item 4; ADMIN ONLY, office UI only). Each week the platform checks
// every record against how long ELE keeps it (the retention rules, server/src/modules/gdpr/retentionRules.ts) and PROPOSES
// the ones past their time. Nothing is deleted or anonymised until an admin confirms a rule's list here (Director
// decision D-GDPR-1). The page shows each rule's proposals with what a confirm would do to each record (delete,
// anonymise, or keep under a hold), lets the admin keep a record by dismissing its proposal (with a reason), and runs
// the weekly check on demand.
//
// THE SERVER DECIDES EVERYTHING SHOWN. Each proposal is re-checked live and planned on the server every time this page
// loads; the confirm sends back only the plan's reference, and the server refuses it if the records have changed since
// (the page then reloads and says so). The page never builds a plan of its own.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { RetentionConfirmResult, RetentionGroupView, RetentionOverview, RetentionRecordView, RetentionResolvedView } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
// A calendar date (dueSince is 00:00 UTC on the day) or an instant is shown as its London day (formatDate).
import { formatDate, formatDateTime } from "../../lib/format";

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The group's counts as one plain sentence, leaving out what is zero. Exported for the test. */
export function groupSummary(g: RetentionGroupView): string {
  const c = g.counts;
  const parts = [
    c.delete > 0 ? `${c.delete} to delete` : null,
    c.anonymise > 0 ? `${c.anonymise} to anonymise` : null,
    c.held > 0 ? `${c.held} kept under a hold` : null,
    c.notDue > 0 ? `${c.notDue} no longer due` : null,
    c.gone > 0 ? `${c.gone} no longer on the platform` : null,
    c.waiting > 0 ? `${c.waiting} waiting for the next confirmation` : null,
  ].filter(Boolean);
  return `${plural(g.open, "record")} proposed${parts.length > 0 ? `: ${parts.join(", ")}` : ""}.`;
}

/** The Drive files a record's erasure deletes (the platform's own copies). The plan binds them, so the page says so. */
function filesDeleted(r: RetentionRecordView): number {
  return r.files.filter((f) => f.action === "delete").length;
}

/** The line under a record whose erasure also deletes its file from Google Drive (review F3). Exported for the test. */
export function driveFileLine(r: RetentionRecordView): string | null {
  const n = r.inPlan ? filesDeleted(r) : 0;
  if (n === 0) return null;
  return n === 1 ? "Its file in Google Drive is permanently deleted too." : `Its ${n} files in Google Drive are permanently deleted too.`;
}

/** The distinct Drive files a confirm of this group deletes, counted once each however many records name one. */
export function groupDriveFiles(g: RetentionGroupView): number {
  const ids = new Set<string>();
  for (const r of g.records) {
    if (r.state !== "due" || !r.inPlan || (r.action !== "delete" && r.action !== "anonymise")) continue;
    for (const f of r.files) if (f.action === "delete") ids.add(f.fileId);
  }
  return ids.size;
}

/** What the last weekly check could not read or work out, as one sentence (review F1). Exported for the test. */
export function incompleteSweepText(inc: NonNullable<RetentionOverview["lastSweepIncomplete"]>): string {
  const parts = [
    inc.recordTypes.length > 0 ? `these records could not be read: ${inc.recordTypes.join(", ")}` : null,
    inc.rules.length > 0 ? `these rules could not be worked out: ${inc.rules.join("; ")}` : null,
  ].filter(Boolean);
  return `The last weekly check did not finish, so records past their time may be missing from this page. In that check ${parts.join(", and ")}. Run the check again, or it will try next week.`;
}

type Section = { key: string; title: string; records: RetentionRecordView[] };

function sectionsOf(g: RetentionGroupView): Section[] {
  const by = (pred: (r: RetentionRecordView) => boolean) => g.records.filter(pred);
  return [
    { key: "delete", title: "To be deleted", records: by((r) => r.state === "due" && r.inPlan && r.action === "delete") },
    { key: "anonymise", title: "To be anonymised (the record stays, the person's details are removed)", records: by((r) => r.state === "due" && r.inPlan && r.action === "anonymise") },
    { key: "held", title: "Kept under a hold", records: by((r) => r.state === "due" && r.inPlan && r.action !== "delete" && r.action !== "anonymise") },
    { key: "waiting", title: "Waiting for the next confirmation", records: by((r) => r.state === "due" && !r.inPlan) },
    { key: "notDue", title: "No longer due (a confirm for this rule, or the next weekly check, closes these)", records: by((r) => r.state === "not_due") },
    { key: "gone", title: "No longer on the platform (a confirm for this rule, or the next weekly check, closes these)", records: by((r) => r.state === "gone") },
  ].filter((s) => s.records.length > 0);
}

export function DataRetentionPage() {
  const [data, setData] = useState<RetentionOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);

  const [confirmFor, setConfirmFor] = useState<RetentionGroupView | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ label: string; result: RetentionConfirmResult } | null>(null);

  const [dismissing, setDismissing] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  const [deleteFor, setDeleteFor] = useState<RetentionResolvedView | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.dataRetention.overview();
      setData(res);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setError("Only an admin can manage data retention, and only from this page.");
      else setError(friendlyApiError(err, "The retention proposals could not be loaded. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function runSweep() {
    setSweeping(true);
    setNotice(null);
    setError(null);
    try {
      const res = await api.dataRetention.runSweep();
      const t = res.totals;
      setNotice(
        `The check has run: ${plural(t.opened + t.reopened, "record")} newly proposed, ${plural(t.stillOpen, "proposal")} still waiting, ${plural(t.lapsed, "proposal")} closed as no longer due.` +
          (res.failedModels.length + res.failedRules.length > 0 ? " Some records could not be checked this time; they will be checked again next week." : ""),
      );
      await load();
    } catch (err) {
      setError(friendlyApiError(err, "The check could not be run. Please try again."));
    } finally {
      setSweeping(false);
    }
  }

  async function doConfirm() {
    const g = confirmFor;
    if (!g || !g.planHash) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const result = await api.dataRetention.confirm(g.ruleId, g.planHash);
      setLastResult({ label: g.label, result });
      setConfirmFor(null);
      await load();
    } catch (err) {
      const code = err instanceof ApiError ? (err.body?.error as string | undefined) : undefined;
      if (code === "plan_changed" || code === "nothing_to_confirm") {
        // The records moved since the page loaded: nothing was erased. Show the list as it is now.
        setConfirmFor(null);
        setNotice(friendlyApiError(err, "The records have changed since the page was loaded, so nothing was erased. Check the list and confirm again."));
        await load();
      } else {
        setConfirmError(friendlyApiError(err, "The confirmation could not be carried out. Nothing was erased. Please try again."));
      }
    } finally {
      setConfirmBusy(false);
    }
  }

  async function dismiss(r: RetentionRecordView) {
    const text = reason.trim();
    if (!text) {
      setRowError({ id: r.proposalId, message: "Say why this record should be kept." });
      return;
    }
    setRowBusy(r.proposalId);
    setRowError(null);
    try {
      await api.dataRetention.dismiss(r.proposalId, text);
      setDismissing(null);
      setReason("");
      await load();
    } catch (err) {
      setRowError({ id: r.proposalId, message: friendlyApiError(err, "The record could not be kept. Please try again.") });
    } finally {
      setRowBusy(null);
    }
  }

  async function restore(p: RetentionResolvedView) {
    setRowBusy(p.proposalId);
    setRowError(null);
    try {
      await api.dataRetention.restore(p.proposalId);
      await load();
    } catch (err) {
      setRowError({ id: p.proposalId, message: friendlyApiError(err, "The proposal could not be put back. Please try again.") });
    } finally {
      setRowBusy(null);
    }
  }

  async function doDelete() {
    const p = deleteFor;
    if (!p) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.dataRetention.remove(p.proposalId);
      setDeleteFor(null);
      await load();
    } catch (err) {
      setDeleteError(friendlyApiError(err, "The proposal could not be deleted. Please try again."));
    } finally {
      setDeleteBusy(false);
    }
  }

  const confirmMessage = useMemo(() => {
    if (!confirmFor) return "";
    const c = confirmFor.counts;
    const what = [c.delete > 0 ? `permanently delete ${plural(c.delete, "record")}` : null, c.anonymise > 0 ? `anonymise ${plural(c.anonymise, "record")}` : null]
      .filter(Boolean)
      .join(" and ");
    const files = groupDriveFiles(confirmFor);
    const drive = files > 0 ? `, and permanently delete ${plural(files, "file")} from Google Drive,` : "";
    return `This will ${what}${drive} under the rule: ${confirmFor.label}. It cannot be undone. Anything kept under a hold is not touched, and Xero is not touched.`;
  }, [confirmFor]);

  const card = "rounded-ele border border-ele-purple-accent bg-ele-surface p-5";

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Data retention"
        subtitle={
          <span className="block max-w-3xl space-y-2">
            <span className="block">
              Each week the platform checks every record against how long we keep it, and lists here anything that is past its
              time, grouped by rule. Nothing is deleted or anonymised until you confirm a rule&apos;s list: the weekly check only
              proposes.
            </span>
            <span className="block">
              Deleted means the record is removed. Anonymised means the business record stays and the person&apos;s name, email,
              phone and address are replaced with &quot;Erased on&quot; and the date. If you want to keep a record, keep it with a
              reason and the check will not propose it again.
            </span>
            {data && <span className="block">{data.xeroNote}</span>}
            {data && (
              <span className="block">
                The one thing removed automatically, with no confirmation, is this: {data.automaticCleanup}. Those are sign-in
                records, not customer data.
              </span>
            )}
          </span>
        }
      />

      <div className={`mb-6 flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${card}`}>
        <div>
          <p className="text-sm text-ele-body" data-testid="last-sweep">
            {data?.lastSweepAt ? `Last weekly check: ${formatDateTime(data.lastSweepAt)}.` : data ? "The weekly check has not run yet." : ""}
          </p>
          {data?.lastSweepIncomplete && (
            <p role="alert" data-testid="last-sweep-incomplete" className="mt-2 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text)]">
              {incompleteSweepText(data.lastSweepIncomplete)}
            </p>
          )}
        </div>
        <Button onClick={() => void runSweep()} disabled={sweeping || loading}>
          {sweeping ? "Checking..." : "Run the check now"}
        </Button>
      </div>

      {error && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mb-4 max-w-3xl rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}

      {lastResult && (
        <section aria-label="Last confirmation" className={`mb-6 max-w-3xl ${card}`}>
          <h2 className="font-heading text-xl text-ele-blue">Done</h2>
          <p className="mt-1 text-sm text-ele-body">
            {plural(lastResult.result.result.deleted, "record")} deleted and {plural(lastResult.result.result.anonymised, "record")} anonymised under: {lastResult.label}
          </p>
          {lastResult.result.result.filesDeleted > 0 && (
            <p className="mt-1 text-sm text-ele-body" data-testid="files-deleted">
              {plural(lastResult.result.result.filesDeleted, "file")} deleted from Google Drive.
            </p>
          )}
          {lastResult.result.filesToRemoveByHand.length > 0 && (
            <>
              <p className="mt-3 text-sm text-ele-strong">These files are in Google Drive and were not deleted. Remove them there by hand if they should go:</p>
              <ul className="mt-1 list-disc pl-5 text-sm text-ele-body">
                {lastResult.result.filesToRemoveByHand.map((f) => (
                  <li key={`${f.fileId}-${f.why}`}>
                    {f.recordType}
                    {f.label ? `: ${f.label}` : ""} (Drive file {f.fileId}){f.why === "drive_refused" ? ": Google Drive refused the delete" : ": the platform did not file it itself"}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {loading && !data && <Loader label="Loading the retention proposals..." shape="panel" />}

      {data && (
        <>
          <section aria-label="Proposed for erasure" className="mb-8">
            <h2 className="mb-3 font-heading text-2xl text-ele-blue">Waiting for your decision</h2>
            {data.groups.length === 0 ? (
              <EmptyState
                className="max-w-3xl"
                message={
                  data.lastSweepIncomplete
                    ? "Nothing is waiting for a decision from the records the last check could read."
                    : "Nothing is waiting for a decision."
                }
              />
            ) : (
              <div className="space-y-5">
                {data.groups.map((g) => (
                  <article key={g.ruleId} aria-label={g.label} className={card}>
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                      <div className="max-w-3xl">
                        <h3 className="font-heading text-lg text-ele-strong">{g.label}</h3>
                        {!g.directorConfirmed && (
                          <StatusPill word="Awaiting the Director's confirmation of this rule" tone="amber" className="mt-1" />
                        )}
                        <p className="mt-1 text-sm text-ele-body">{groupSummary(g)}</p>
                      </div>
                      <Button
                        variant="danger"
                        className="shrink-0"
                        onClick={() => {
                          setConfirmError(null);
                          setConfirmFor(g);
                        }}
                        disabled={!g.planHash}
                      >
                        Confirm for this rule
                      </Button>
                    </div>
                    {g.error && (
                      <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                        {g.error}
                      </p>
                    )}
                    {sectionsOf(g).map((s) => (
                      <div key={s.key} className="mt-4">
                        <h4 className="text-xs uppercase tracking-widest text-ele-blue">
                          {s.title} ({s.records.length})
                        </h4>
                        <ul className="mt-2 space-y-3">
                          {s.records.map((r) => (
                            <li key={r.proposalId} className="text-sm">
                              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                  <span className="text-ele-strong">{r.label ?? "No longer on the platform"}</span>
                                  <span className="ml-2 text-xs text-ele-body">
                                    {r.recordType}, due since {formatDate(r.dueSince)}
                                  </span>
                                  {r.reason && <span className="mt-0.5 block text-xs text-ele-body">{r.reason}</span>}
                                  {r.action === "anonymise" && r.fields.length > 0 && (
                                    <span className="mt-0.5 block text-xs text-ele-body">Removes: {r.fields.join(", ")}</span>
                                  )}
                                  {driveFileLine(r) && <span className="mt-0.5 block text-xs text-ele-strong">{driveFileLine(r)}</span>}
                                </div>
                                {r.state === "due" && dismissing !== r.proposalId && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="shrink-0"
                                    onClick={() => {
                                      setDismissing(r.proposalId);
                                      setReason("");
                                      setRowError(null);
                                    }}
                                  >
                                    Keep this record
                                  </Button>
                                )}
                              </div>
                              {dismissing === r.proposalId && (
                                <div className="mt-2 max-w-xl">
                                  <label className="mb-1 block text-xs" htmlFor={`keep-${r.proposalId}`}>
                                    Why should it be kept? Please do not name anyone.
                                  </label>
                                  <textarea
                                    id={`keep-${r.proposalId}`}
                                    value={reason}
                                    maxLength={500}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                                  />
                                  <div className="mt-2 flex gap-2">
                                    <Button size="sm" disabled={rowBusy === r.proposalId} onClick={() => void dismiss(r)}>
                                      {rowBusy === r.proposalId ? "Saving..." : "Keep it"}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setDismissing(null)}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {rowError?.id === r.proposalId && (
                                <p role="alert" className="mt-1 text-xs text-ele-danger">
                                  {rowError.message}
                                </p>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </article>
                ))}
              </div>
            )}
          </section>

          <section aria-label="Kept records" className="mb-8">
            <h2 className="mb-3 font-heading text-2xl text-ele-blue">Kept ({data.dismissed.length})</h2>
            {data.dismissed.length === 0 ? (
              <p className="text-sm text-ele-body">No record has been kept against a proposal.</p>
            ) : (
              <ul className={`space-y-3 ${card}`}>
                {data.dismissed.map((p) => (
                  <li key={p.proposalId} className="flex flex-col gap-2 text-sm sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <span className="text-ele-strong">{p.label ?? "No longer on the platform"}</span>
                      <span className="ml-2 text-xs text-ele-body">
                        {p.recordType}, kept {formatDate(p.resolvedAt)}
                        {p.resolvedBy ? ` by ${p.resolvedBy}` : ""}
                      </span>
                      <span className="mt-0.5 block text-xs text-ele-body">Why: {p.dismissReason}</span>
                      <span className="mt-0.5 block text-xs text-ele-body">Rule: {p.ruleLabel}</span>
                      {rowError?.id === p.proposalId && (
                        <span role="alert" className="mt-1 block text-xs text-ele-danger">
                          {rowError.message}
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button variant="secondary" size="sm" disabled={rowBusy === p.proposalId} onClick={() => void restore(p)}>
                        Put back in the list
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteFor(p);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label="Closed proposals" className="mb-8">
            <h2 className="mb-3 font-heading text-2xl text-ele-blue">Closed as no longer due ({data.lapsedTotal})</h2>
            {data.lapsed.length === 0 ? (
              <p className="text-sm text-ele-body">None.</p>
            ) : (
              <>
                {data.lapsedTotal > data.lapsed.length && <p className="mb-2 text-xs text-ele-body">The most recent {data.lapsed.length} are listed.</p>}
                <ul className={`space-y-2 ${card}`}>
                  {data.lapsed.map((p) => (
                    <li key={p.proposalId} className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span>
                        <span className="text-ele-strong">{p.label ?? "No longer on the platform"}</span>
                        <span className="ml-2 text-xs text-ele-body">
                          {p.recordType}, closed {formatDate(p.resolvedAt)}
                        </span>
                      </span>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteFor(p);
                        }}
                      >
                        Delete
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="mt-2 text-xs text-ele-body">{plural(data.actionedTotal, "proposal")} carried out so far (each is in the audit trail).</p>
          </section>

          <details className={`max-w-3xl ${card}`}>
            <summary className="cursor-pointer font-heading text-lg text-ele-blue">What the weekly check looks at</summary>
            <ul className="mt-3 space-y-2 text-sm">
              {data.rules.map((r) => (
                <li key={r.id}>
                  <span className="text-ele-strong">{r.label}</span>
                  {!r.directorConfirmed && <span className="ml-2 text-xs text-[var(--ele-amber-text)]">Awaiting the Director&apos;s confirmation</span>}
                  {r.open > 0 && <span className="ml-2 text-xs text-ele-body">{plural(r.open, "proposal")} waiting</span>}
                  {r.notApplicable !== null && r.notApplicable > 0 && (
                    <span className="block text-xs text-ele-body">{plural(r.notApplicable, "record")} kept with no retention date, never proposed</span>
                  )}
                  {r.unknown !== null && r.unknown > 0 && (
                    <span className="block text-xs text-ele-body">{plural(r.unknown, "record")} with no recorded date to count from, never proposed</span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        </>
      )}

      <ConfirmDeleteModal
        open={confirmFor !== null}
        title="Confirm for this rule"
        message={confirmMessage}
        confirmLabel="Confirm and erase"
        variant="destructive"
        busy={confirmBusy}
        error={confirmError}
        onConfirm={() => void doConfirm()}
        onCancel={() => setConfirmFor(null)}
      />

      <ConfirmDeleteModal
        open={deleteFor !== null}
        title={deleteFor?.status === "dismissed" ? "Delete this kept record's note" : "Delete this closed proposal"}
        message={
          deleteFor?.status === "dismissed"
            ? "This removes the note that the record is kept. The record itself is not touched, but the weekly check may propose it again."
            : "This removes the closed proposal from the list. The record itself is not touched."
        }
        confirmLabel="Delete"
        variant="destructive"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void doDelete()}
        onCancel={() => setDeleteFor(null)}
      />
    </div>
  );
}
